import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { FaultListLanguage } from "../../../../../models/api/fault/faultListLanguage";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { FaultListService } from "../../../../../services/api/fault-list/fault-list.service";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { HttpErrorResponse } from "@angular/common/http";
import { MessageService } from "@openng/optimus-ui/api";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-language",
  standalone: true,
  imports: [InputTextModule, ReactiveFormsModule, CheckboxModule, ButtonComponent, TranslateModule],
  templateUrl: "./language.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./language.component.scss",
})
export class LanguageComponent {
  private dialogConfig = inject(DynamicDialogConfig);
  private faultListService = inject(FaultListService);
  private ref = inject(DynamicDialogRef);
  private message = inject(MessageService);
  private translate = inject(TranslateService);

  private language: FaultListLanguage = this.dialogConfig.data.language;
  public actionType = this.dialogConfig.data.actionType;

  public form = new FormGroup({
    languageName: new FormControl(this.language.languageName, Validators.required),
    languageData: new FormControl(this.language.languageData, Validators.required),
    languageCode: new FormControl(this.language.languageCode, Validators.required),
  });

  /**
   * Closes the dialog.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Saves the language data to the server either by adding a new language or by editing an existing one.
   * If the form is valid, it will create a new language or update an existing one.
   * If the save is successful, the dialog will be closed.
   * If the save fails, an error message will be shown and the dialog will remain open.
   */
  public saveLanguage() {
    const formValues = this.form.value;
    if (this.form.valid && formValues.languageCode && formValues.languageName && formValues.languageData) {
      if (this.actionType === "add") {
        const language: FaultListLanguage = {
          languageCode: formValues.languageCode,
          languageName: formValues.languageName,
          languageData: formValues.languageData,
        };
        this.addLanguage(language);
      } else if (this.actionType === "edit") {
        const language: FaultListLanguage = {
          languageName: formValues.languageName,
          languageData: formValues.languageData,
        };
        this.editLanguage(formValues.languageCode, language);
      }
    }
  }

  /**
   * Adds a new language to the server.
   * If the save is successful, it will close the dialog and show a success message.
   * If the save fails, it will show an error message and keep the dialog open.
   * @param language The language to be added.
   */
  private addLanguage(language: FaultListLanguage) {
    this.faultListService.postFaultListLanguage(language).subscribe({
      next: () => {
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("common.success"),
          detail: this.translate.instant("fault_allocation.fault_lists.language_management.language_add_success"),
          life: 4500,
        });
        this.ref.close("refresh");
      },
      error: (err: HttpErrorResponse) => {
        this.message.add({
          key: "global",
          severity: "error",
          summary: "Fehler",
          detail: this.translate.instant("error_messages." + err.error.code),
          life: 4500,
        });
      },
    });
  }

  /**
   * Edits an existing language on the server.
   * If the save is successful, it will close the dialog and show a success message.
   * If the save fails, it will show an error message and keep the dialog open.
   * @param languageCode The code of the language to be edited.
   * @param language The updated language object.
   */
  private editLanguage(languageCode: string, language: FaultListLanguage) {
    this.faultListService.putFaultListLanguage(languageCode, language).subscribe({
      next: () => {
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("common.success"),
          detail: this.translate.instant("fault_allocation.fault_lists.language_management.language_edit_success"),
          life: 4500,
        });
        this.ref.close("refresh");
      },
      error: (err: HttpErrorResponse) => {
        this.message.add({
          key: "global",
          severity: "error",
          summary: "Fehler",
          detail: this.translate.instant("error_messages." + err.error.code),
          life: 4500,
        });
      },
    });
  }
}
